import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MessageSquare, Send, CheckCircle } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, Button, Input, Select, FormField } from '../components/ui';

const CreateTicket = () => {
  const [formData, setFormData] = useState({
    subject: '',
    category: 'general',
    priority: 'medium',
    description: '',
    email: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const categories = [
    { value: 'general', label: 'General Inquiry' },
    { value: 'booking', label: 'Booking Issue' },
    { value: 'payment', label: 'Payment Issue' },
    { value: 'technical', label: 'Technical Issue' },
    { value: 'other', label: 'Other' }
  ];

  const priorities = [
    { value: 'low', label: 'Low', color: 'text-green-600' },
    { value: 'medium', label: 'Medium', color: 'text-yellow-600' },
    { value: 'high', label: 'High', color: 'text-orange-600' },
    { value: 'urgent', label: 'Urgent', color: 'text-red-600' }
  ];

  const validateForm = () => {
    const newErrors = {};

    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    } else if (formData.description.trim().length < 10) {
      newErrors.description = 'Description must be at least 10 characters';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    // Simulate API call
    setTimeout(() => {
      // In a real app, this would send the ticket to a backend
      console.log('Ticket submitted:', formData);
      
      // Store ticket in localStorage for demo purposes
      const tickets = JSON.parse(localStorage.getItem('topgame_tickets') || '[]');
      const newTicket = {
        id: Date.now(),
        ...formData,
        status: 'open',
        createdAt: new Date().toISOString()
      };
      tickets.push(newTicket);
      localStorage.setItem('topgame_tickets', JSON.stringify(tickets));

      setIsSubmitting(false);
      setIsSubmitted(true);

      // Reset form
      setFormData({
        subject: '',
        category: 'general',
        priority: 'medium',
        description: '',
        email: ''
      });
    }, 1500);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  if (isSubmitted) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto">
          <Card className="p-8 text-center">
            <CheckCircle className="h-16 w-16 text-ga-success mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-ga-text-primary mb-2">Ticket Created Successfully!</h2>
            <p className="text-ga-text-muted mb-6">
              Your support ticket has been submitted. We'll get back to you soon.
            </p>
            <div className="flex gap-4 justify-center">
              <Button onClick={() => setIsSubmitted(false)}>
                Create Another Ticket
              </Button>
              <Button as={Link} to="/dashboard/help-center" variant="ghost">
                Back to Help Center
              </Button>
            </div>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-ga-text-primary mb-2 flex items-center gap-3">
            <MessageSquare className="h-8 w-8 text-ga-primary" />
            Create Support Ticket
          </h1>
          <p className="text-ga-text-muted">
            Describe your issue and we'll help you resolve it
          </p>
        </div>

        {/* Form */}
        <Card>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Subject */}
            <FormField label="Subject" required error={errors.subject}>
              <Input
                type="text"
                value={formData.subject}
                onChange={(e) => handleChange('subject', e.target.value)}
                error={!!errors.subject}
                placeholder="Brief description of your issue"
              />
            </FormField>

            {/* Category and Priority */}
            <div className="grid grid-cols-2 gap-4">
              <FormField label="Category" required>
                <Select
                  value={formData.category}
                  onChange={(e) => handleChange('category', e.target.value)}
                >
                  {categories.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </Select>
              </FormField>

              <FormField label="Priority" required>
                <Select
                  value={formData.priority}
                  onChange={(e) => handleChange('priority', e.target.value)}
                >
                  {priorities.map(pri => (
                    <option key={pri.value} value={pri.value}>{pri.label}</option>
                  ))}
                </Select>
              </FormField>
            </div>

            {/* Email */}
            <FormField label="Email Address" required error={errors.email}>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                error={!!errors.email}
                placeholder="your.email@example.com"
              />
            </FormField>

            {/* Description */}
            <FormField
              label="Description"
              required
              error={errors.description}
              hint="Minimum 10 characters required"
            >
              <textarea
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                className={`ga-input w-full rounded-ga-sm ${errors.description ? 'border-ga-danger focus:ring-ga-danger/30' : ''}`}
                rows="6"
                placeholder="Please provide detailed information about your issue..."
              />
            </FormField>

            {/* Submit Button */}
            <div className="flex gap-4">
              <Button
                type="submit"
                disabled={isSubmitting}
                className="flex-1"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-ga-text-on-primary"></div>
                    <span>Submitting...</span>
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4" />
                    <span>Submit Ticket</span>
                  </>
                )}
              </Button>
              <Button as={Link} to="/dashboard/help-center" variant="ghost">
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default CreateTicket;

